// reports.service.ts
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'apps/piqet-angular/src/environments/environment';
import { Observable } from 'rxjs';
import { saveAs } from 'file-saver';
import { MIME_TYPES } from '@piqet-angular/shared';

interface PartialUpdateRequest {
  projectId: number;
  userId?: number;
  organizationId?: number;
  changedFields: { [key: string]: any };
}

// Payload sent to POST /Report/ExportProject (project summary variant).
// scenarioIds is always an array of integer ids — empty when the user
// hasn't selected any.
export interface ExportProjectSummaryPayload {
  projectId: number;
  scenarioIds: number[];
  isShowReportDetails: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  apiUrl = environment.url;

  constructor(private http: HttpClient) {}

  // Existing methods...
  getReportGenerationInitialData(projectId: any) {
    return this.http
      .get<any>(`${this.apiUrl}/Report/GetReportInitialFormData?projectId=${projectId}`)
      .toPromise();
  }

  getReportGenerationFormData(projectId: number): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/Report/GetReportGenerationFormData?projectId=${projectId}`
    );
  }

  getReportGenerationData(
    projectId: any,
    reportGenerationTypeId: any,
    newReportGenrationTypeId: any
  ) {
    return this.http
      .get<any>(
        `${this.apiUrl}/Report/ReportFormData?projectId=${projectId}&reportGenerationTypeId=${reportGenerationTypeId}&newReportGenrationTypeId=${newReportGenrationTypeId}`
      )
      .toPromise();
  }

  getReportInterpretationData(
    projectId: any,
    reportGenerationTypeId: any,
    newReportGenrationTypeId: any
  ) {
    return this.http.get<any>(
      `${this.apiUrl}/Report/ReportFormData?projectId=${projectId}&reportGenerationTypeId=${reportGenerationTypeId}&newReportGenrationTypeId=${newReportGenrationTypeId}`
    );
  }

  getInterpretationDetail(projectId: any) {
    return this.http.get<any>(
      `${this.apiUrl}/Report/GetInterpretationDetail?projectId=${projectId}`
    );
  }

  // Original method - keep for backward compatibility
  addInterpretationDetail(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/Report/AddInterpretationDetail`, payload);
  }

  // New optimized method for partial updates
  updateInterpretationDetailPartial(payload: PartialUpdateRequest) {
    return this.http.post<any>(`${this.apiUrl}/Report/UpdateInterpretationDetailPartial`, payload);
  }

  saveFunctionalUnitText(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/Report/SaveOrUpdateFunctionalUnitText`, payload);
  }

  getReportGenerationDataForExcelSummery(projectId: any) {
    return this.http.get<any>(
      `${this.apiUrl}/Report/ExcelSummaryReportFormData?projectId=${projectId}`
    );
  }

  getReportGenerationDataForSlide(projectId: any, reportGenerationTypeId: any) {
    return this.http.get<any>(
      `${this.apiUrl}/Report/SlidesReportFormData?projectId=${projectId}&reportGenerationTypeId=${reportGenerationTypeId}`
    );
  }

  generateFullReport(fullReportPayload: any) {
    return this.http
      .post<any>(`${this.apiUrl}/Report/FullDetailedReport`, fullReportPayload)
      .toPromise();
  }

  PDFGenerator(fullReportPayload: any) {
    return this.http
      .get<any>(`${this.apiUrl}/PDFGenerator/FullReport?reuqestString=${fullReportPayload}`)
      .toPromise();
  }

  generateCompactReport(compactReportPayload: any) {
    return this.http
      .post<any>(`${this.apiUrl}/Report/PiqetCompactReport`, compactReportPayload)
      .toPromise();
  }

  generateProductWasteReport(productWasteReportPayload: any) {
    return this.http
      .post<any>(`${this.apiUrl}/Report/ProductWasteReport`, productWasteReportPayload)
      .toPromise();
  }

  getExcelSummaryLink(excelSummaryPayload: any) {
    return this.http.post<any>(`${this.apiUrl}/Report/ExportCsv`, excelSummaryPayload).toPromise();
  }

  // Existing GET — kept for backwards compatibility with the standard
  // "Export to Excel" button (Report Format = Export raw data).
  exportToExcelSummaryLink(projectId: number) {
    return this.http.get<any>(`${this.apiUrl}/Report/ExportProject?projectId=${projectId}`);
  }

  // POST variant used by the "Export project summary" Report Format option.
  // The payload model is shaped on the frontend so adding fields later
  // (functional unit, indicator filters, etc.) doesn't require touching
  // every call site — just extend ExportProjectSummaryPayload.
  exportProjectSummary(payload: ExportProjectSummaryPayload) {
    return this.http.post<any>(`${this.apiUrl}/Report/ExportProject`, payload);
  }

  // Deletes a generated project-export file from the server after the client has
  // downloaded it. fileName is the bare name returned with the export result.
  deleteExportProjectFile(fileName: string) {
    return this.http.post<any>(
      `${this.apiUrl}/Report/DeleteExportProjectFile?fileName=${encodeURIComponent(fileName)}`,
      {}
    );
  }

  getProductExcelSummaryLink(excelSummaryPayload: any) {
    return this.http
      .post<any>(`${this.apiUrl}/ReportForProduct/ExportProductCsv`, excelSummaryPayload)
      .toPromise();
  }

  getProductExcelSummaryLinkNew(excelSummaryPayload: any) {
    return this.http
      .post<any>(`${this.apiUrl}/Report/ExportProjectDetails`, excelSummaryPayload)
      .toPromise();
  }

  // Generate Simple Slides Deck Report (Slide Summary) Data
  generateSimpleSlidesDeckReport(simpleSlidesDeckReportPayload: any) {
    return this.http
      .post<any>(`${this.apiUrl}/Report/SlideSummaryReport`, simpleSlidesDeckReportPayload)
      .toPromise();
  }

  generateSlidesReport(slidesReportPayload: any) {
    return this.http
      .post<any>(`${this.apiUrl}/Report/SlidesReport`, slidesReportPayload)
      .toPromise();
  }

  generateCarbonFootprintReport(carbonFootprintReportPayload: any) {
    return this.http
      .post<any>(`${this.apiUrl}/Report/CarbonFootprintReport`, carbonFootprintReportPayload)
      .toPromise();
  }

  GetChartCompareTypeProductWise(isExcludeProductImpacts: boolean) {
    return this.http.get<any>(
      `${this.apiUrl}/Report/GetChartCompareTypeProductWise?isExcludeProductImpacts=${isExcludeProductImpacts}`
    );
  }

  getProductDetails(productId: any, isExport: boolean) {
    return this.http
      .get<any>(`${this.apiUrl}/Report/ProductDetails?productId=${productId}&isExport=${isExport}`)
      .toPromise();
  }

  getIngredientDatasourceDetails(productId: any, isExport: boolean) {
    const scenarioId = 0;
    return this.http
      .get<any>(
        `${this.apiUrl}/Report/IngredientDatasourceDetails?productId=${productId}&isExport=${isExport}&&ScenarioIds=${scenarioId}`
      )
      .toPromise();
  }

  getScenarioDetails(
    sId: any,
    isExport: boolean,
    isProductContent: boolean,
    isExcludeProductImpacts: boolean
  ) {
    return this.http
      .get<any>(
        `${this.apiUrl}/Report/ScenarioDetails?sId=${sId}&isExport=${isExport}&isProductContent=${isProductContent}&isExcludeProductImpacts=${isExcludeProductImpacts}`
      )
      .toPromise();
  }

  getDataSourceDetails(sId: any, isExport: boolean, scenario: any) {
    return this.http
      .get<any>(
        `${this.apiUrl}/Report/DataSourceDetails?sId=${sId}&isExport=${isExport}&scenario=${scenario}`
      )
      .toPromise();
  }

  getOrganizationReportSetting(
    isCustomReportSetting: any,
    reportGenerationTypeId: any,
    projectReportSettingId: any
  ) {
    return this.http.get<any>(
      `${this.apiUrl}/Report/GetOrganizationReportSetting?IsCustomType=${isCustomReportSetting}&reportGenerationTypeId=${reportGenerationTypeId}&projectReportSettingId=${projectReportSettingId}`
    );
  }

  getReportGeneration() {
    return this.http.get<any>(`${this.apiUrl}/Report/GetReportGeneration`).toPromise();
  }

  getReportTypeByReportFormat(reportFormateId: any) {
    return this.http.get<any>(
      `${this.apiUrl}/Report/GetReportTypeByReportFormat?reportFormatId=${reportFormateId}`
    );
  }

  getReportGenrationTypeByTypeIdAndFormateId(reportFormateId: any, typeId: any, isCustom: any) {
    return this.http
      .get<any>(
        `${this.apiUrl}/Report/GetReportGenrationTypeByTypeIdAndFormateId?reportFormatId=${reportFormateId}&typeId=${typeId}&isCustom=${isCustom}`
      )
      .toPromise();
  }

  GetCustomReportSettingIndicatorItems(customereportSettingId: any) {
    return this.http
      .get<any>(
        `${this.apiUrl}/Report/GetCustomReportSettingIndicatorItems?customereportSettingId=${customereportSettingId}`
      )
      .toPromise();
  }

  getChartTypeOptions(count: number) {
    return this.http
      .get<any>(`${this.apiUrl}/Report/GetChartTypeOptions?count=${count}`)
      .toPromise();
  }

  GetChartTypeOptionsProjectReport(count: number) {
    return this.http
      .get<any>(`${this.apiUrl}/Report/GetChartTypeOptionsProjectReport?count=${count}`)
      .toPromise();
  }

  saveInterpretation(projectId: any, interpretation: any) {
    return this.http.get<any>(
      `${this.apiUrl}/Report/SaveInterpretation?projectId=${projectId}&interpretation=${interpretation}`
    );
  }

  barChartNewOrganization(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/Report/BarChartNewOrganization`, payload);
  }

  spiderChartNewOrganization(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/Report/SpiderChartNewOrganization`, payload);
  }

  exportCsvOrganizationReporting(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/Report/exportCsvOrganizationReporting`, payload);
  }

  pieChartNewNewOrganization(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/Report/PieChartNewNewOrganization`, payload);
  }

  getChartCompareTypeProductWise(isExcludeProductImpacts: boolean) {
    return this.http.get<any>(
      `${this.apiUrl}/Report/GetChartCompareTypeProductWise?isExcludeProductImpacts=${isExcludeProductImpacts}`
    );
  }

  updateIsShowOrganizationPersonalAchievement(isshow: boolean) {
    return this.http.post<any>(
      `${this.apiUrl}/Report/UpdateIsShowOrganizationPersonalAchievement?isshow=${isshow}`,
      {}
    );
  }

  GetSummaryData() {
    return this.http.get<any>(`${this.apiUrl}/Report/GetSummaryData`).toPromise();
  }

  directGenertePDF(payloadToString: any, generateURL: string): Observable<Blob> {
    const propertiesToDelete = [
      'flag',
      'isCheckeportListByTypeById',
      'isCheckeportListByFormateById',
      'reportType',
      'loaderMessage',
    ];
    propertiesToDelete.forEach(prop => {
      if (prop in payloadToString) {
        delete payloadToString[prop];
      }
    });

    const jsonString = JSON.stringify(payloadToString);
    const base64String = btoa(unescape(encodeURIComponent(jsonString)));

    const headers = new HttpHeaders({
      'Content-Type': MIME_TYPES.APPLICATION_PDF,
    });

    return this.http.get(`${this.apiUrl}/${generateURL}=${base64String}`, {
      headers,
      responseType: 'blob',
    });
  }

  processPdfBlobResponse(
    blob: Blob,
    options: {
      payload: any;
      fileName?: string;
      fileExtension?: string;
      isMail?: boolean;
      onError: (payload: any, error: any) => void;
      onSuccess?: (message?: string) => void;
      onMailSuccess?: () => void;
    }
  ): void {
    if (blob.type === MIME_TYPES.APPLICATION_JSON) {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const errorResponse = JSON.parse(reader.result as string);
          options.onError(options.payload, errorResponse);
        } catch {
          options.onError(options.payload, {
            message: 'An error occurred while generating the report.',
          });
        }
      };
      reader.readAsText(blob);
      return;
    }

    if (options.isMail || options.payload.isMail) {
      if (options.onMailSuccess) {
        options.onMailSuccess();
      } else if (options.onSuccess) {
        options.onSuccess('Your report will be shared via your email shortly');
      }
    } else if (options.fileName) {
      const extension = options.fileExtension || '.pdf';
      let fileName = options.fileName;

      if (fileName?.includes('.')) {
        fileName = fileName.split('.')[0];
      }

      fileName = fileName + extension;

      saveAs(blob, fileName);

      if (options.onSuccess) {
        options.onSuccess();
      }
    }
  }

  processMailReportResponse(
    blob: Blob,
    options: {
      payload: any;
      onError: (payload: any, error: any) => void;
      onSuccess?: (message?: string) => void;
      onMailSuccess?: () => void;
    }
  ): void {
    // Handle both JSON and PDF responses as success cases for mail reports
    if (blob.type === MIME_TYPES.APPLICATION_JSON || blob.type === MIME_TYPES.APPLICATION_PDF) {
      if (options.onMailSuccess) {
        options.onMailSuccess();
      } else if (options.onSuccess) {
        options.onSuccess('Your report will be shared via your email shortly');
      }
    } else {
      options.onError(options.payload, {
        message: 'Invalid or empty response received.',
      });
    }
  }

  addUpdateDataQualityAssessment(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/report/AddUpdateDataQualityAssessment`, payload);
  }

  saveLCAReportDetails(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/Report/SaveLCAReportDetails`, payload).toPromise();
  }

  getMaterialDataByScenarioIds(names: any, projectId: Number) {
    return this.http.get<any>(
      `${this.apiUrl}/Component/GetMaterialDataByScenarioIds?names=${names}&projectId=${projectId}`
    );
  }

  generateISOReport(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/Report/FullISOReport`, payload).toPromise();
  }

  generateCustomReportNew(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/Report/GenerateCustomReport`, payload).toPromise();
  }

  getAllMyOrganizationTeamListAndUsers() {
    return this.http
      .get<any>(`${this.apiUrl}/MyOrganizationTeam/GetAllMyOrganizationTeamListAndUsers`)
      .toPromise();
  }

  searchMembers(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/Report/SearchMembers`, payload);
  }

  compareExcelReport(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/ReportComparison/CompareExcelReport`, payload);
  }

  getCompareExcelReportData() {
    return this.http.get<any>(`${this.apiUrl}/ReportComparison/GetCompareExcelReportData`);
  }
  getCompareExcelReportDetailsById(compareId: any) {
    return this.http.get<any>(
      `${this.apiUrl}/ReportComparison/GetCompareExcelReportDetailsById?compareId=${compareId}`
    );
  }
  exportComparisonReport(compareId: any) {
    return this.http.get<any>(
      `${this.apiUrl}/ReportComparison/ExportComparisonReport?compareId=${compareId}`
    );
  }

  deleteExportCompareFile(fileName: string) {
    return this.http.post<any>(
      `${this.apiUrl}/ReportComparison/DeleteExportProjectFile?fileName=${encodeURIComponent(
        fileName
      )}`,
      {}
    );
  }
  getProductReportFormData(productConfigId: any) {
    return this.http
      .get<any>(
        `${this.apiUrl}/ReportForProduct/ReportFormDataforProduct?productConfigId=${productConfigId}`
      )
      .toPromise();
  }
  checkReportFromDataForProduct(productConfigId: any) {
    return this.http
      .get<any>(
        `${this.apiUrl}/ReportForProduct/CheckReportFromDataForProduct?productConfigId=${productConfigId}`
      )
      .toPromise();
  }

  generateLCAProductReport(): Observable<Blob> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/pdf',
    });

    return this.http.get(`${this.apiUrl}/PDFGenerator/ProductLCAReport`, {
      headers,
      responseType: 'blob',
    });
  }

  getGlanceReportData(productConfigId: any) {
    return this.http.get<any>(
      `${this.apiUrl}/ChartAtGlance/GetGlanceReportFormData?productConfigId=${productConfigId}`
    );
  }

  generateBarChartForProduct(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/ChartAtGlance/BarChartForProduct`, payload);
  }

  generateSpiderChartForProduct(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/ChartAtGlance/SpiderChartForProduct`, payload);
  }
  generateCAGReport(payload: any) {
    return this.http.post<any>(`${this.apiUrl}/ChartAtGlance/GenerateCAGReport`, payload);
  }
}
